// Zakázky (docs/SPEC.md 2.5): zod schémata vstupů a tvary odpovědí API.
import { z } from 'zod'
import { jobSourceSchema, jobStageSchema } from './enums'
import { addressSchema } from './org'
import type { Contact, PromiseSummary, TimelineEntry } from './clients'
import type { Attachment } from './attachments'

const money = z.number().int().min(0).max(1_000_000_000_00)

export const createJobSchema = z.object({
  clientId: z.string().uuid('Vyber klienta'),
  contactId: z.string().uuid().optional().nullable(),
  title: z.string().trim().min(2, 'Zadej název zakázky').max(200),
  description: z.string().trim().max(5000).optional().or(z.literal('')),
  stage: jobStageSchema.default('lead'),
  siteAddress: addressSchema.optional().nullable(),
  assigneeId: z.string().uuid().optional().nullable(),
  estimatedAmountMinor: money.optional().nullable(),
  vatRate: z.number().int().min(0).max(100).optional().nullable(),
  source: jobSourceSchema.default('manual'),
  sourceDetail: z.string().trim().max(200).optional().or(z.literal('')),
})
export type CreateJobInput = z.infer<typeof createJobSchema>

export const updateJobSchema = createJobSchema
  .omit({ clientId: true, stage: true })
  .extend({
    clientId: z.string().uuid().optional(),
    finalAmountMinor: money.optional().nullable(),
    nextServiceAt: z.string().datetime({ offset: true }).optional().nullable(),
    marketingConsent: z.boolean().optional(),
  })
  .partial()
export type UpdateJobInput = z.infer<typeof updateJobSchema>

export const changeStageSchema = z.object({
  stage: jobStageSchema,
  /** Při přechodu do Hotovo: souhlas zákazníka s použitím fotek (SPEC 2.5). */
  marketingConsent: z.boolean().optional(),
})
export type ChangeStageInput = z.infer<typeof changeStageSchema>

export const LOST_REASONS = [
  'price',
  'competitor',
  'postponed',
  'no_response',
  'out_of_scope',
  'other',
] as const
export const lostJobSchema = z.object({
  reason: z.enum(LOST_REASONS),
  note: z.string().trim().max(500).optional().or(z.literal('')),
})
export type LostJobInput = z.infer<typeof lostJobSchema>

export const listJobsQuerySchema = z.object({
  q: z.string().trim().max(200).optional(),
  stage: jobStageSchema.optional(),
  clientId: z.string().uuid().optional(),
  /** true = jen ztracené, false nebo prázdné = jen aktivní. */
  lost: z
    .preprocess((v) => (v === 'true' ? true : v === 'false' ? false : v), z.boolean())
    .optional(),
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(500).default(100),
})
export type ListJobsQuery = z.infer<typeof listJobsQuerySchema>

export interface JobSummary {
  id: string
  title: string
  stage: 'lead' | 'quote' | 'agreed' | 'in_progress' | 'done' | 'invoiced'
  clientId: string
  clientName: string
  siteAddress: { street?: string; city?: string; zip?: string } | null
  /** Nejbližší termín: událost nebo slib. */
  nextDate: string | null
  /** Vznikla z návrhu Buddyho a má neschválené části (magenta proužek). */
  hasPendingProposal: boolean
  source: string
  estimatedAmountMinor: number | null
  lostAt: string | null
  updatedAt: string
}

export interface JobEventSummary {
  id: string
  type: 'visit' | 'work' | 'meeting' | 'other'
  title: string
  startAt: string
  endAt: string
  allDay: boolean
  location: string | null
}

export interface JobTaskSummary {
  id: string
  title: string
  dueAt: string | null
  status: 'open' | 'done'
}

export interface JobDetail extends JobSummary {
  description: string | null
  contactId: string | null
  contact: Contact | null
  assigneeId: string | null
  finalAmountMinor: number | null
  currency: string
  vatRate: number | null
  sourceDetail: string | null
  campaignId: string | null
  firstResponseAt: string | null
  lostReason: string | null
  nextServiceAt: string | null
  parentJobId: string | null
  marketingConsent: boolean
  sourceInteractionId: string | null
  sourceQuote: string | null
  promises: PromiseSummary[]
  events: JobEventSummary[]
  tasks: JobTaskSummary[]
  attachments: Attachment[]
  timeline: TimelineEntry[]
  createdAt: string
}
